// Hook for fetching and managing comments
// T106: Implement useCreateComment hook
// T109: Add optimistic update to comment UI

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import { getContentReplies, getCommentTree } from '@/services/comments/commentsClient'
import {
  createComment,
  updateComment,
  deleteComment,
  type CreateCommentParams,
  type UpdateCommentParams,
  type DeleteCommentParams,
} from '@/services/steemit/commentService'
import { useAuthStore } from '@/stores/authStore'
import type { Comment } from '@/types/comment'

interface UseCommentsOptions {
  author: string
  permlink: string
  enabled?: boolean
  includeReplies?: boolean
  maxDepth?: number
}

interface UseCommentsResult {
  comments: Comment[]
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

/**
 * Hook to fetch comments for a post or comment
 */
export function useComments({
  author,
  permlink,
  enabled = true,
  includeReplies = true,
  maxDepth = 3,
}: UseCommentsOptions): UseCommentsResult {
  const {
    data: comments = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.comments(author, permlink),
    queryFn: async () => {
      if (includeReplies) {
        return getCommentTree(author, permlink, maxDepth)
      }
      return getContentReplies(author, permlink)
    },
    enabled: enabled && !!author && !!permlink,
    staleTime: 1000 * 60 * 2, // 2 minutes
  })

  return {
    comments,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  }
}

interface CreateCommentOptions {
  parentAuthor: string
  parentPermlink: string
  rootAuthor?: string
  rootPermlink?: string
}

/**
 * Hook to create a new comment
 * T106: Implement useCreateComment hook
 */
export function useCreateComment(options: CreateCommentOptions) {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  const { parentAuthor, parentPermlink, rootAuthor, rootPermlink } = options

  // Determine which query to invalidate (root post comments)
  const queryAuthor = rootAuthor || parentAuthor
  const queryPermlink = rootPermlink || parentPermlink

  return useMutation({
    mutationFn: async (body: string) => {
      if (!user) {
        throw new Error('로그인이 필요합니다')
      }

      const params: CreateCommentParams = {
        author: user.username,
        postingKey: user.postingKey,
        parentAuthor,
        parentPermlink,
        body,
      }

      const result = await createComment(params)

      if (!result.success) {
        throw new Error(result.error || '댓글 작성에 실패했습니다')
      }

      return result
    },
    // T109: Optimistic update
    onMutate: async (body: string) => {
      if (!user) return

      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.comments(queryAuthor, queryPermlink),
      })

      // Snapshot the previous value
      const previousComments = queryClient.getQueryData<Comment[]>(
        queryKeys.comments(queryAuthor, queryPermlink)
      )

      // Create optimistic comment
      const optimisticComment: Comment = {
        linkId: Date.now(),
        author: user.username,
        permlink: `temp-${Date.now()}`,
        body,
        created: new Date(),
        lastUpdate: new Date(),
        cashoutTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        parentAuthor,
        parentPermlink,
        rootAuthor: queryAuthor,
        rootPermlink: queryPermlink,
        depth: parentAuthor === queryAuthor ? 1 : 2,
        children: [],
        childCount: 0,
        upvoteCount: 0,
        downvoteCount: 0,
        payout: 0,
        authorReputation: 0,
        isMuted: false,
        url: '',
      }

      // Add to cache
      queryClient.setQueryData<Comment[]>(
        queryKeys.comments(queryAuthor, queryPermlink),
        (old = []) => {
          // If it's a reply to a comment, find the parent and add to its children
          if (parentAuthor !== queryAuthor) {
            return addReplyToComment(old, parentAuthor, parentPermlink, optimisticComment)
          }
          // If it's a direct reply to the post, add to the end
          return [...old, optimisticComment]
        }
      )

      return { previousComments }
    },
    onError: (_error, _body, context) => {
      // Rollback on error
      if (context?.previousComments) {
        queryClient.setQueryData(
          queryKeys.comments(queryAuthor, queryPermlink),
          context.previousComments
        )
      }
    },
    onSettled: () => {
      // Refetch to sync with server
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments(queryAuthor, queryPermlink),
      })
      // Also invalidate post to update comment count
      queryClient.invalidateQueries({
        queryKey: queryKeys.post(queryAuthor, queryPermlink),
      })
    },
  })
}

/**
 * Hook to update a comment
 */
export function useUpdateComment() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  return useMutation({
    mutationFn: async (params: Omit<UpdateCommentParams, 'author' | 'postingKey'> & {
      rootAuthor: string
      rootPermlink: string
    }) => {
      if (!user) {
        throw new Error('로그인이 필요합니다')
      }

      const result = await updateComment({
        author: user.username,
        postingKey: user.postingKey,
        permlink: params.permlink,
        parentAuthor: params.parentAuthor,
        parentPermlink: params.parentPermlink,
        body: params.body,
      })

      if (!result.success) {
        throw new Error(result.error || '댓글 수정에 실패했습니다')
      }

      return { ...result, rootAuthor: params.rootAuthor, rootPermlink: params.rootPermlink }
    },
    onSuccess: (data) => {
      // Invalidate the comments query
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments(data.rootAuthor, data.rootPermlink),
      })
    },
  })
}

/**
 * Hook to delete a comment
 */
export function useDeleteComment() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  return useMutation({
    mutationFn: async (params: Omit<DeleteCommentParams, 'author' | 'postingKey'> & {
      rootAuthor: string
      rootPermlink: string
    }) => {
      if (!user) {
        throw new Error('로그인이 필요합니다')
      }

      const result = await deleteComment({
        author: user.username,
        postingKey: user.postingKey,
        permlink: params.permlink,
      })

      if (!result.success) {
        throw new Error(result.error || '댓글 삭제에 실패했습니다')
      }

      return { ...result, rootAuthor: params.rootAuthor, rootPermlink: params.rootPermlink }
    },
    onSuccess: (data) => {
      // Invalidate the comments query
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments(data.rootAuthor, data.rootPermlink),
      })
      // Also invalidate post to update comment count
      queryClient.invalidateQueries({
        queryKey: queryKeys.post(data.rootAuthor, data.rootPermlink),
      })
    },
  })
}

/**
 * Helper function to add a reply to a nested comment
 */
function addReplyToComment(
  comments: Comment[],
  parentAuthor: string,
  parentPermlink: string,
  newComment: Comment
): Comment[] {
  return comments.map((comment) => {
    if (comment.author === parentAuthor && comment.permlink === parentPermlink) {
      return {
        ...comment,
        children: [...(comment.children || []), newComment],
        childCount: (comment.childCount || 0) + 1,
      }
    }
    if (comment.children && comment.children.length > 0) {
      return {
        ...comment,
        children: addReplyToComment(comment.children, parentAuthor, parentPermlink, newComment),
      }
    }
    return comment
  })
}
